/**
 * ProgramRequirements — the program "Program Requirements" tab.
 *
 * MVP redesign (DEV-409): the Requirements specification organised into the
 * five scorecard categories. This is a light re-skin of the scorecard editor —
 * it keeps the full item-level functionality (inline title/description edits,
 * importance, draggable numeric thresholds, ordered-categorical / categorical /
 * boolean criteria, add/remove) that the previous scorecard page had, all
 * wired to the backend via `ScorecardItemCard`.
 *
 * Renders inside ProgramLayout's AppShell <main>; it owns no page chrome.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, ChevronDown, ChevronRight } from 'lucide-react'
import { tokenManager } from '../../lib/api'
import { useViewportContext } from '../../hooks/useViewportContext'
import { Spinner } from '../../components/ui'
import { cn } from '../../lib/utils'
import ScorecardItemCard, { ScorecardItemData } from '../../components/scorecard/ScorecardItemCard'
import SpecSearchDropdown from '../../components/scorecard/SpecSearchDropdown'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// ── Types ──────────────────────────────────────────────────────────

interface ScorecardSection {
  id: number
  title: string
  description: string
  order: number
  items: ScorecardItemData[]
}

interface Scorecard {
  id: number
  name: string
  scorecard_type: string
  sections: ScorecardSection[]
  solution_family?: { id: string; name: string } | null
}

interface Program {
  id: number
  name: string
  status: string
  status_display: string
  creation_status: string
  requirement_profile?: { agency_need?: string | null } | null
}

// ── Scorecard categories (the five program-level scorecard types) ───
// Labels mirror the backend SCORECARD_TYPE_CHOICES display names; foundational
// ("Basic Requirements") leads, per the redesign.

type TabKey =
  | 'foundational'
  | 'technical'
  | 'manufacturing_readiness'
  | 'vc_metrics'
  | 'dod_partnership'

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'foundational', label: 'Basic Requirements' },
  { key: 'technical', label: 'Technical' },
  { key: 'manufacturing_readiness', label: 'Production Readiness' },
  { key: 'vc_metrics', label: 'Company Stability' },
  { key: 'dod_partnership', label: 'DoW Partnership' },
]

// Scorecards the user builds by hand (vs. template-copied) get the
// "Add Section" / "Add Item" affordances.
const USER_BUILT_TYPES: TabKey[] = ['foundational', 'technical']

const COMPLETED_CREATION = ['ready', 'published']

// ── Page ───────────────────────────────────────────────────────────

const ProgramRequirements: React.FC = () => {
  const { programId } = useParams<{ programId: string }>()
  const [program, setProgram] = useState<Program | null>(null)
  const [scorecards, setScorecards] = useState<Scorecard[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabKey>('technical')

  const [addingToSection, setAddingToSection] = useState<number | null>(null)
  const [addingSection, setAddingSection] = useState(false)
  const [newSectionTitle, setNewSectionTitle] = useState('')
  const [sectionError, setSectionError] = useState<string | null>(null)
  // Section ids that are collapsed (default: all expanded).
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set())

  const toggleSection = (id: number) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Program-level scorecard for a tab (family technical scorecards live on the
  // Solutions page, not here).
  const scorecardFor = useMemo(
    () => (key: TabKey) =>
      scorecards.find((sc) => sc.scorecard_type === key && !sc.solution_family),
    [scorecards]
  )

  const countFor = (key: TabKey) =>
    scorecardFor(key)?.sections.reduce((sum, s) => sum + s.items.length, 0) ?? 0

  const activeScorecard = scorecardFor(activeTab)
  const isUserBuiltTab = USER_BUILT_TYPES.includes(activeTab)

  // Tier 2: viewport context for the agent
  useViewportContext(
    'requirements',
    () => ({
      program: program
        ? {
            id: program.id,
            name: program.name,
            status: program.status,
            creation_status: program.creation_status,
          }
        : null,
      mission_need: program?.requirement_profile?.agency_need?.slice(0, 2000) || null,
      active_tab: activeTab,
      scorecards: scorecards.map((sc) => ({
        id: sc.id,
        type: sc.scorecard_type,
        name: sc.name,
        solution_family: sc.solution_family?.name ?? null,
        sections: sc.sections.map((s) => ({
          title: s.title,
          item_count: s.items.length,
          items: s.items.map((i) => ({
            title: i.title,
            importance: i.importance,
            spec_name: i.spec_name,
          })),
        })),
      })),
    }),
    [program, activeTab, scorecards]
  )

  // ── Data loading ───────────────────────────────────────────────

  const fetchData = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!programId) return
    // Background refetches (agent edits) keep the current UI; only the initial
    // load shows the full-page spinner.
    if (!background) setLoading(true)
    const token = tokenManager.getAccessToken()
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    try {
      const [progRes, scRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/programs/${programId}/`, { headers }),
        // Includes program scorecards + pathway-shared (vc / dod) scorecards.
        fetch(`${API_BASE_URL}/api/programs/${programId}/scorecards/`, { headers }),
      ])
      if (progRes.ok) setProgram(await progRes.json())
      if (scRes.ok) {
        const data = await scRes.json()
        setScorecards(Array.isArray(data) ? data : data.results || [])
      }
    } catch (err) {
      console.error('Error loading program requirements:', err)
    } finally {
      setLoading(false)
    }
  }, [programId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Refetch when the agent mutates requirement or scorecard data.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (['scorecard', 'program', 'requirement'].includes(detail?.resource))
        fetchData({ background: true })
    }
    window.addEventListener('agentDataChanged', handler)
    return () => window.removeEventListener('agentDataChanged', handler)
  }, [fetchData])

  // Land on the first populated tab once data first arrives. Runs only on the
  // initial load — otherwise every mutation (e.g. adding an empty section, or
  // deleting the last item) would bounce the user off the tab they're editing.
  const landedOnTab = useRef(false)
  useEffect(() => {
    if (landedOnTab.current || !scorecards.length) return
    landedOnTab.current = true
    if (countFor(activeTab) > 0) return
    const firstWithItems = TAB_CONFIG.find((t) => countFor(t.key) > 0)
    if (firstWithItems) setActiveTab(firstWithItems.key)
  }, [scorecards])

  // ── Item operations (debounced PATCH, matches the scorecard editor) ──

  const pendingUpdates = useRef<
    Record<number, { updates: Partial<ScorecardItemData>; timer: ReturnType<typeof setTimeout> }>
  >({})

  const flushUpdate = useCallback(
    async (itemId: number, updates: Partial<ScorecardItemData>) => {
      try {
        const token = tokenManager.getAccessToken()
        const res = await fetch(`${API_BASE_URL}/api/scorecard-items/${itemId}/`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        })
        // The local state is already optimistically updated; if the server
        // rejected the write, refetch to resync rather than leave the UI lying.
        if (!res.ok) {
          console.error('Failed to update item:', res.status)
          fetchData({ background: true })
        }
      } catch (err) {
        console.error('Failed to update item:', err)
      }
    },
    [fetchData]
  )

  const updateItem = useCallback(
    (itemId: number, updates: Partial<ScorecardItemData>) => {
      // Optimistic local update for responsiveness (e.g. threshold dragging).
      setScorecards((prev) =>
        prev.map((sc) => ({
          ...sc,
          sections: sc.sections.map((section) => ({
            ...section,
            items: section.items.map((item) =>
              item.id === itemId ? { ...item, ...updates } : item
            ),
          })),
        }))
      )

      // Debounce the API call, merging rapid changes for the same item.
      const pending = pendingUpdates.current[itemId]
      if (pending) {
        clearTimeout(pending.timer)
        pending.updates = { ...pending.updates, ...updates }
      } else {
        pendingUpdates.current[itemId] = { updates: { ...updates }, timer: setTimeout(() => {}, 0) }
        clearTimeout(pendingUpdates.current[itemId].timer)
      }
      pendingUpdates.current[itemId].timer = setTimeout(() => {
        const merged = pendingUpdates.current[itemId]?.updates
        delete pendingUpdates.current[itemId]
        if (merged) flushUpdate(itemId, merged)
      }, 400)
    },
    [flushUpdate]
  )

  // Flush any debounced edits on unmount so navigating away within the 400ms
  // window doesn't silently drop the user's last change.
  const flushUpdateRef = useRef(flushUpdate)
  flushUpdateRef.current = flushUpdate
  useEffect(() => {
    const pending = pendingUpdates.current
    return () => {
      Object.entries(pending).forEach(([id, { updates, timer }]) => {
        clearTimeout(timer)
        flushUpdateRef.current(Number(id), updates)
      })
    }
  }, [])

  const deleteItem = useCallback(async (itemId: number) => {
    try {
      const token = tokenManager.getAccessToken()
      const res = await fetch(`${API_BASE_URL}/api/scorecard-items/${itemId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      // Only drop the item locally if the server actually deleted it.
      if (!res.ok) {
        console.error('Failed to delete item:', res.status)
        return
      }
      setScorecards((prev) =>
        prev.map((sc) => ({
          ...sc,
          sections: sc.sections.map((section) => ({
            ...section,
            items: section.items.filter((item) => item.id !== itemId),
          })),
        }))
      )
    } catch (err) {
      console.error('Failed to delete item:', err)
    }
  }, [])

  const addItemFromSpec = useCallback(
    async (sectionId: number, spec: any) => {
      try {
        const token = tokenManager.getAccessToken()
        const section = activeScorecard?.sections.find((s) => s.id === sectionId)
        const maxOrder = Math.max(0, ...(section?.items.map((i) => i.order) || [0]))
        const response = await fetch(`${API_BASE_URL}/api/scorecard-items/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            section: sectionId,
            title: spec.name,
            description: spec.description || '',
            importance: 'important',
            order: maxOrder + 1,
            spec: spec.id,
            lower_threshold: spec.typical_min != null ? Number(spec.typical_min) : null,
            upper_threshold: spec.typical_max != null ? Number(spec.typical_max) : null,
          }),
        })
        if (response.ok) {
          const newItem = await response.json()
          setScorecards((prev) =>
            prev.map((sc) => ({
              ...sc,
              sections: sc.sections.map((section) =>
                section.id === sectionId
                  ? {
                      ...section,
                      items: [
                        ...section.items,
                        { ...newItem, spec_name: spec.name, spec_unit: spec.unit },
                      ],
                    }
                  : section
              ),
            }))
          )
        }
      } catch (err) {
        console.error('Failed to add item:', err)
      }
      setAddingToSection(null)
    },
    [activeScorecard]
  )

  const addSection = useCallback(async () => {
    const title = newSectionTitle.trim()
    if (!activeScorecard || !title) return
    setSectionError(null)
    try {
      const token = tokenManager.getAccessToken()
      const maxOrder = Math.max(0, ...(activeScorecard.sections.map((s) => s.order) || [0]))
      const response = await fetch(`${API_BASE_URL}/api/scorecard-sections/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scorecard: activeScorecard.id, title, order: maxOrder + 1 }),
      })
      if (response.ok) {
        const newSection = await response.json()
        setScorecards((prev) =>
          prev.map((sc) =>
            sc.id === activeScorecard.id
              ? { ...sc, sections: [...sc.sections, { ...newSection, items: newSection.items || [] }] }
              : sc
          )
        )
        setNewSectionTitle('')
        setAddingSection(false)
      } else {
        const data = await response.json().catch(() => ({}))
        const msg =
          (Array.isArray(data.title) && data.title[0]) ||
          data.detail ||
          (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) ||
          'Could not add section. A section with that title may already exist.'
        setSectionError(msg)
      }
    } catch (err) {
      console.error('Failed to add section:', err)
      setSectionError('Could not add section.')
    }
  }, [activeScorecard, newSectionTitle])

  // ── Render ─────────────────────────────────────────────────────

  if (!programId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">Invalid program.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const isBuilding = program ? !COMPLETED_CREATION.includes(program.creation_status) : false
  const hasAnyScorecard = scorecards.some((sc) => !sc.solution_family)
  const sections = (activeScorecard?.sections ?? []).slice().sort((a, b) => a.order - b.order)

  return (
    <div className="px-6 py-10 lg:px-10">
      <div className="max-w-[640px]">
        <section>
          <div className="space-y-3">
            <h2 className="text-xl font-medium text-foreground">Requirements</h2>
            <p className="text-sm text-muted-foreground">
              Specify precise technical and compliance requirements for the product.
            </p>
          </div>

          {/* Category tabs */}
          <div className="mt-6 flex gap-2 overflow-x-auto">
            {TAB_CONFIG.map((tab) => {
              const sc = scorecardFor(tab.key)
              const count = countFor(tab.key)
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'whitespace-nowrap border-b px-4 py-2 text-sm transition-colors',
                    isActive
                      ? 'border-foreground font-medium text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                  {sc ? ` (${count})` : ''}
                </button>
              )
            })}
          </div>

          {/* Active tab content */}
          <div className="mt-8">
            {activeScorecard ? (
              <div className="space-y-8">
                {sections.map((section) => {
                  const open = !collapsedSections.has(section.id)
                  return (
                  <div key={section.id}>
                    {/* Collapsible section header (the section's own title) */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="mb-3 flex w-full items-center gap-2 text-left"
                    >
                      {open ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium text-foreground">{section.title}</span>
                      <span className="text-xs text-muted-foreground">{section.items.length}</span>
                    </button>

                    {open && (
                    <div className="space-y-2 pl-6">
                      {section.items
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((item) => (
                          <ScorecardItemCard
                            key={item.id}
                            item={item}
                            mode="edit"
                            onUpdate={updateItem}
                            onDelete={deleteItem}
                          />
                        ))}

                      {/* Add Item */}
                      {isUserBuiltTab &&
                        (addingToSection === section.id ? (
                          <div className="mt-2">
                            <SpecSearchDropdown
                              onSelectSpec={(spec) => addItemFromSpec(section.id, spec)}
                              onCreateNew={(name) =>
                                addItemFromSpec(section.id, {
                                  id: null,
                                  name,
                                  description: '',
                                  unit: '',
                                  typical_min: null,
                                  typical_max: null,
                                })
                              }
                              category={activeTab === 'technical' ? 'technical' : undefined}
                              programId={programId}
                              placeholder="Search specs to add as requirement..."
                            />
                            <button
                              onClick={() => setAddingToSection(null)}
                              className="mt-1 text-xs text-muted-foreground hover:text-foreground"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddingToSection(section.id)}
                            className="flex w-full items-center gap-1.5 rounded-lg px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add Item
                          </button>
                        ))}
                    </div>
                    )}
                  </div>
                  )
                })}

                {/* Add Section (user-built scorecards only) */}
                {isUserBuiltTab &&
                  (addingSection ? (
                    <div className="space-y-1 pt-2">
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={newSectionTitle}
                          onChange={(e) => {
                            setNewSectionTitle(e.target.value)
                            setSectionError(null)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') addSection()
                            if (e.key === 'Escape') {
                              setAddingSection(false)
                              setNewSectionTitle('')
                              setSectionError(null)
                            }
                          }}
                          placeholder="Section title (e.g. Performance)"
                          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                        />
                        <button
                          onClick={addSection}
                          disabled={!newSectionTitle.trim()}
                          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setAddingSection(false)
                            setNewSectionTitle('')
                            setSectionError(null)
                          }}
                          className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                      {sectionError && <p className="text-xs text-destructive">{sectionError}</p>}
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setAddingSection(true)
                        setSectionError(null)
                      }}
                      className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Section
                    </button>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {isBuilding && !hasAnyScorecard
                  ? 'Requirements are still being set up for this program.'
                  : 'No requirements have been defined in this category yet.'}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default ProgramRequirements
